import React, { useMemo, useRef } from 'react';

import { diff } from 'deep-object-diff';
import PropTypes from 'prop-types';

import useDocumentVisibilityChange from 'components/hooks/useDocumentVisibilityChange';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import { useProjectsDashboardContext } from 'components/contexts/ProjectsDashboardContext';
import ArchivedProjectsIndex from 'components/projectsDashboard/components/ArchivedProjectsIndex';
import FoldersIndex from 'components/projectsDashboard/components/FoldersIndex';
import ProjectsIndex from 'components/projectsDashboard/components/ProjectsIndex';
import {
  buildBulkUpdateFilterSelectionConfigurationParams,
  buildBulkUpdateTableConfigurationParams,
} from 'components/projectsDashboard/helpers';
import useProjectFilters, {
  buildBaseProjectFilters,
} from 'components/projectsDashboard/hooks/useProjectFilters';
import useProjectTableConfiguration from 'components/projectsDashboard/hooks/useProjectTableConfiguration';

export default function IndexWrapper({
  activeSidebarMenuSection,
  domainProjectsDashboardFilterSelectionConfiguration,
  domainProjectsDashboardTableConfiguration,
  hasFolders,
  indexWrapperRef,
  onArchiveProjectsClick,
  onDebouncedFiltersChange,
  onDeleteFolderClick,
  onFolderClick,
  onGroupIntoFolderClick,
  onMoveToFolderClick,
  onNewFolderClick,
  onProjectArchiveClose,
  onProjectDestroyClose,
  onProjectToggle,
  onProjectsPageChange,
  onRenameFolderClick,
  onSelectAllProjectsToggle,
  onSidebarMenuSectionClick,
  selectedFolderId,
  selectedProjectIds,
}) {
  const currentActor = useCurrentActor();

  const singularRAMSName =
    currentActor.division.attributes.singularRAMSName || 'RAMS';

  const {
    folderPolicy,
    projectsDashboardPolicy,
    seedDashboardConfigurableFields,
  } = useProjectsDashboardContext();

  const configurableFieldOptionFilterSelections =
    domainProjectsDashboardFilterSelectionConfiguration.data?.relationships.configurableFieldOptionFilterSelections.data.map(
      (fs) =>
        domainProjectsDashboardFilterSelectionConfiguration.included
          .projectsDashboardConfigurableFieldOptionFilterSelection[fs.id],
    );

  const dateFilterSelections =
    domainProjectsDashboardFilterSelectionConfiguration.data?.relationships.dateFilterSelections.data.map(
      (fs) =>
        domainProjectsDashboardFilterSelectionConfiguration.included
          .projectsDashboardDateFilterSelection[fs.id],
    );
  const folderFilterSelections =
    domainProjectsDashboardFilterSelectionConfiguration.data?.relationships.folderFilterSelections.data.map(
      (fs) =>
        domainProjectsDashboardFilterSelectionConfiguration.included
          .projectsDashboardFolderFilterSelection[fs.id],
    );
  const optionFilterSelections =
    domainProjectsDashboardFilterSelectionConfiguration.data?.relationships.optionFilterSelections.data.map(
      (fs) =>
        domainProjectsDashboardFilterSelectionConfiguration.included
          .projectsDashboardOptionFilterSelection[fs.id],
    );
  const userFilterSelections =
    domainProjectsDashboardFilterSelectionConfiguration.data?.relationships.userFilterSelections.data.map(
      (fs) =>
        domainProjectsDashboardFilterSelectionConfiguration.included
          .projectsDashboardUserFilterSelection[fs.id],
    );

  const {
    handleProjectFilterAdd,
    handleProjectFilterRemove,
    handleProjectFilterValueAdd,
    handleProjectFilterValueChange,
    handleProjectFilterValueRemove,
    handleProjectFiltersCleanup,
    handleProjectFiltersReset,
    projectFilters,
  } = useProjectFilters({
    configurableFieldOptionFilterSelections,
    dashboardConfigurableFields: seedDashboardConfigurableFields.data,
    dateFilterSelections,
    folderFilterSelections,
    optionFilterSelections,
    userFilterSelections,
  });

  const tableColumnConfigurations =
    domainProjectsDashboardTableConfiguration.data?.relationships.tableColumnConfigurations.data.map(
      (tc) =>
        domainProjectsDashboardTableConfiguration.included
          .projectsDashboardTableColumnConfiguration[tc.id],
    );
  const tableSortConfiguration =
    domainProjectsDashboardTableConfiguration.data?.relationships
      .tableSortConfiguration.data &&
    domainProjectsDashboardTableConfiguration.included
      .projectsDashboardTableSortConfiguration[
      domainProjectsDashboardTableConfiguration.data?.relationships
        .tableSortConfiguration.data.id
    ];

  const {
    handleProjectTableColumnOrderChange,
    handleProjectTableColumnSizingChange,
    handleProjectTableColumnSortChange,
    handleProjectTableColumnVisibilityChange,
    handleProjectTableConfigurationsReset,
    projectTableConfiguration,
  } = useProjectTableConfiguration({
    dashboardConfigurableFields: seedDashboardConfigurableFields.data,
    singularRAMSName,
    tableColumnConfigurations,
    tableSortConfiguration,
    ...projectsDashboardPolicy,
  });

  const baseProjectFilters = buildBaseProjectFilters({
    dashboardConfigurableFields: seedDashboardConfigurableFields.data,
    singularRAMSName,
  });

  const visibleProjectFilters = useMemo(() => {
    const hiddenProjectFilters = [];

    if (!!selectedFolderId || !projectsDashboardPolicy.isFolderConfigurable)
      hiddenProjectFilters.push('folderFilter');
    if (!projectsDashboardPolicy.isApprovalConfigurable)
      hiddenProjectFilters.push('approvalStatusFilter', 'approverFilter');
    if (!projectsDashboardPolicy.isReviewConfigurable)
      hiddenProjectFilters.push(
        'reviewStatusFilter',
        'reviewDateFilter',
        'reviewerFilter',
      );
    if (!projectsDashboardPolicy.isSignatureConfigurable)
      hiddenProjectFilters.push('signatureStatusFilter');

    return Array.from(baseProjectFilters.keys()).filter(
      (filterKey) => !hiddenProjectFilters.includes(filterKey),
    );
  }, [
    selectedFolderId,
    ...Object.values(projectsDashboardPolicy),
    ...Array.from(baseProjectFilters.keys()),
  ]);

  // used by document visibilty change hook to diff initial and current project filters
  const initialDocumentVisibilityChangeProjectFilters = useRef({
    filters: projectFilters,
    visibleFilters: visibleProjectFilters,
  });
  const documentVisibilityChangeProjectFilters = useRef();
  documentVisibilityChangeProjectFilters.current = {
    filters: projectFilters,
    visibleFilters: visibleProjectFilters,
  };

  useDocumentVisibilityChange((visibilityState) => {
    if (visibilityState === 'visible') {
      initialDocumentVisibilityChangeProjectFilters.current =
        documentVisibilityChangeProjectFilters.current;
    }

    const documentVisibilityChangeProjectFiltersDiff = diff(
      initialDocumentVisibilityChangeProjectFilters.current,
      documentVisibilityChangeProjectFilters.current,
    );

    if (
      visibilityState === 'hidden' &&
      Object.keys(documentVisibilityChangeProjectFiltersDiff).length > 0
    ) {
      const bulkUpdateFilterSelectionConfigurationParams =
        buildBulkUpdateFilterSelectionConfigurationParams(
          documentVisibilityChangeProjectFilters.current,
        );

      fetch(
        `/companies/${currentActor.division.id}/projects_dashboard_filter_selection_configurations/bulk_updates`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            authenticity_token: (
              document.querySelector('meta[name="csrf-token"]') || {}
            ).content,
            bulk_update: bulkUpdateFilterSelectionConfigurationParams,
          }),
          keepalive: true,
        },
      );
    }
  });

  // used by document visibilty change hook to diff initial and current table configuration
  const initialDocumentVisibilityChangeProjectTableConfiguration = useRef({
    columnOrder: projectTableConfiguration.columnOrder,
    columnVisibility: projectTableConfiguration.columnVisibility,
    columnSort: projectTableConfiguration.columnSort,
    columnSizingDefaults: projectTableConfiguration.columnSizingDefaults,
    columnSizing: projectTableConfiguration.columnSizing,
    isReset: projectTableConfiguration.isReset,
  });
  const documentVisibilityChangeProjectTableConfiguration = useRef();
  documentVisibilityChangeProjectTableConfiguration.current = {
    columnOrder: projectTableConfiguration.columnOrder,
    columnVisibility: projectTableConfiguration.columnVisibility,
    columnSort: projectTableConfiguration.columnSort,
    columnSizingDefaults: projectTableConfiguration.columnSizingDefaults,
    columnSizing: projectTableConfiguration.columnSizing,
    isReset: projectTableConfiguration.isReset,
  };

  useDocumentVisibilityChange((visibilityState) => {
    if (visibilityState === 'visible') {
      initialDocumentVisibilityChangeProjectTableConfiguration.current =
        documentVisibilityChangeProjectTableConfiguration.current;
    }

    const documentVisibilityChangeProjectTableConfigurationDiff = diff(
      initialDocumentVisibilityChangeProjectTableConfiguration.current,
      documentVisibilityChangeProjectTableConfiguration.current,
    );

    if (
      visibilityState === 'hidden' &&
      Object.keys(documentVisibilityChangeProjectTableConfigurationDiff)
        .length > 0
    ) {
      const bulkUpdateTableConfigurationParams =
        documentVisibilityChangeProjectTableConfiguration.current.isReset ?
          { columns: [], sort: null }
        : buildBulkUpdateTableConfigurationParams({
            tableConfiguration: {
              ...documentVisibilityChangeProjectTableConfiguration.current,
              columnSizing:
                documentVisibilityChangeProjectTableConfigurationDiff.columnSizing,
            },
          });

      fetch(
        `/companies/${currentActor.division.id}/projects_dashboard_table_configurations/bulk_updates`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            authenticity_token: (
              document.querySelector('meta[name="csrf-token"]') || {}
            ).content,
            bulk_update: bulkUpdateTableConfigurationParams,
          }),
          keepalive: true,
        },
      );
    }
  });

  return (
    <div
      className='tw-w-full tw-flex-1 tw-overflow-y-auto tw-pb-18 tw-pt-6'
      ref={indexWrapperRef}
    >
      {activeSidebarMenuSection === 'folders' && folderPolicy.canIndex ?
        <FoldersIndex
          onDeleteClick={onDeleteFolderClick}
          onFolderClick={({ id }) => {
            onFolderClick({
              folderId: id,
              section: 'recents',
            });
          }}
          onNewClick={onNewFolderClick}
          onRenameClick={onRenameFolderClick}
        />
      : activeSidebarMenuSection === 'archive' ?
        <ArchivedProjectsIndex
          onCurrentSectionClick={() => {
            onSidebarMenuSectionClick({ section: 'current' });
          }}
        />
      : <ProjectsIndex
          baseFilters={baseProjectFilters}
          columnOrder={projectTableConfiguration.columnOrder}
          columnSizing={projectTableConfiguration.columnSizing}
          columnSort={projectTableConfiguration.columnSort}
          columnVisibility={projectTableConfiguration.columnVisibility}
          columns={projectTableConfiguration.columns}
          filters={projectFilters}
          folderId={selectedFolderId}
          hasFolders={hasFolders}
          key={selectedFolderId}
          onArchiveProjectsClick={onArchiveProjectsClick}
          onColumnOrderChange={handleProjectTableColumnOrderChange}
          onColumnSizingChange={handleProjectTableColumnSizingChange}
          onColumnSortChange={handleProjectTableColumnSortChange}
          onColumnVisibilityChange={handleProjectTableColumnVisibilityChange}
          onDebouncedFiltersChange={onDebouncedFiltersChange}
          onDeleteFolderClick={onDeleteFolderClick}
          onFilterAdd={handleProjectFilterAdd}
          onFilterRemove={handleProjectFilterRemove}
          onFilterValueAdd={handleProjectFilterValueAdd}
          onFilterValueChange={handleProjectFilterValueChange}
          onFilterValueRemove={handleProjectFilterValueRemove}
          onGroupIntoFolderClick={onGroupIntoFolderClick}
          onMoveToFolderClick={onMoveToFolderClick}
          onProjectArchiveClose={onProjectArchiveClose}
          onProjectDestroyClose={onProjectDestroyClose}
          onProjectToggle={onProjectToggle}
          onProjectsFilterSidepanelClose={handleProjectFiltersCleanup}
          onProjectsPageChange={onProjectsPageChange}
          onResetTableToDefault={() => {
            handleProjectFiltersReset();
            handleProjectTableConfigurationsReset();
          }}
          onSectionClick={onSidebarMenuSectionClick}
          onSelectAllProjectsToggle={onSelectAllProjectsToggle}
          selectedProjectIds={selectedProjectIds}
          visibleFilters={visibleProjectFilters}
        />
      }
    </div>
  );
}

IndexWrapper.propTypes = {
  activeSidebarMenuSection: PropTypes.string.isRequired,
  domainProjectsDashboardFilterSelectionConfiguration: PropTypes.shape({
    data: PropTypes.object.isRequired,
    included: PropTypes.object.isRequired,
  }),
  domainProjectsDashboardTableConfiguration: PropTypes.shape({
    data: PropTypes.object.isRequired,
    included: PropTypes.object.isRequired,
  }),
  hasFolders: PropTypes.bool.isRequired,
  indexWrapperRef: PropTypes.object.isRequired,
  onArchiveProjectsClick: PropTypes.func.isRequired,
  onDebouncedFiltersChange: PropTypes.func.isRequired,
  onDeleteFolderClick: PropTypes.func.isRequired,
  onFolderClick: PropTypes.func.isRequired,
  onGroupIntoFolderClick: PropTypes.func.isRequired,
  onMoveToFolderClick: PropTypes.func.isRequired,
  onNewFolderClick: PropTypes.func.isRequired,
  onProjectArchiveClose: PropTypes.func.isRequired,
  onProjectDestroyClose: PropTypes.func.isRequired,
  onProjectToggle: PropTypes.func.isRequired,
  onProjectsPageChange: PropTypes.func.isRequired,
  onRenameFolderClick: PropTypes.func.isRequired,
  onSelectAllProjectsToggle: PropTypes.func.isRequired,
  onSidebarMenuSectionClick: PropTypes.func.isRequired,
  selectedFolderId: PropTypes.string,
  selectedProjectIds: PropTypes.array.isRequired,
};
